import { useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  HostedAgentRoutineFormState,
  HostedAgentRoutineScheduleForm,
} from "../utils/hosted-agent-routines"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import {
  applyHostedAgentRoutineSchedule,
  HOSTED_AGENT_WEEKDAY_DEFAULTS,
  HOSTED_AGENT_WEEKDAY_KEYS,
  readHostedAgentRoutineSchedule,
  validateHostedAgentRoutine,
} from "../utils/hosted-agent-routines"

// Friendly schedule picker for one routine (B1). Keeps the frequency/time/
// weekday selection in local state so partial input (a half-typed time) never
// flips the frequency selector, while pushing a resolved cron/interval up to
// the routine on every change.
export function HostedAgentRoutineScheduleEditor({
  routine,
  onReplace,
}: {
  routine: HostedAgentRoutineFormState
  onReplace: (next: HostedAgentRoutineFormState) => void
}) {
  const { t } = useTranslation()
  const [schedule, setSchedule] = useState<HostedAgentRoutineScheduleForm>(() =>
    readHostedAgentRoutineSchedule(routine)
  )
  const scheduleInvalid = validateHostedAgentRoutine(routine).includes("schedule")

  const update = (changes: Partial<HostedAgentRoutineScheduleForm>) => {
    const next = { ...schedule, ...changes }
    setSchedule(next)
    onReplace(applyHostedAgentRoutineSchedule(routine, next))
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="space-y-2">
        <Label htmlFor={`routine-frequency-${routine.id}`}>
          {t("adminDashboard.hostedAgents.routinesTab.frequencyLabel", {
            defaultValue: "Schedule",
          })}
        </Label>
        <Select
          value={schedule.frequency}
          onValueChange={(value) =>
            update({ frequency: value as HostedAgentRoutineScheduleForm["frequency"] })
          }
        >
          <SelectTrigger className="w-full sm:w-64" id={`routine-frequency-${routine.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="interval">
              {t("adminDashboard.hostedAgents.routinesTab.frequencyInterval", {
                defaultValue: "Every N minutes",
              })}
            </SelectItem>
            <SelectItem value="daily">
              {t("adminDashboard.hostedAgents.routinesTab.frequencyDaily", {
                defaultValue: "Daily at a time",
              })}
            </SelectItem>
            <SelectItem value="weekly">
              {t("adminDashboard.hostedAgents.routinesTab.frequencyWeekly", {
                defaultValue: "Weekly on a day",
              })}
            </SelectItem>
            <SelectItem value="advanced">
              {t("adminDashboard.hostedAgents.routinesTab.frequencyAdvanced", {
                defaultValue: "Advanced (cron)",
              })}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {schedule.frequency === "interval" ? (
        <div className="space-y-2">
          <Label htmlFor={`routine-interval-${routine.id}`}>
            {t("adminDashboard.hostedAgents.routinesTab.intervalLabel", {
              defaultValue: "Interval (minutes)",
            })}
          </Label>
          <Input
            className="w-40"
            id={`routine-interval-${routine.id}`}
            inputMode="numeric"
            min={1}
            type="number"
            value={String(schedule.intervalMinutes)}
            onChange={(event) => update({ intervalMinutes: Number(event.target.value) || 0 })}
          />
        </div>
      ) : null}

      {schedule.frequency === "daily" || schedule.frequency === "weekly" ? (
        <div className="flex flex-wrap gap-4">
          {schedule.frequency === "weekly" ? (
            <div className="space-y-2">
              <Label htmlFor={`routine-weekday-${routine.id}`}>
                {t("adminDashboard.hostedAgents.routinesTab.weekdayLabel", {
                  defaultValue: "Day of week",
                })}
              </Label>
              <Select
                value={schedule.weekday}
                onValueChange={(value) => update({ weekday: value })}
              >
                <SelectTrigger className="w-44" id={`routine-weekday-${routine.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOSTED_AGENT_WEEKDAY_KEYS.map((day) => (
                    <SelectItem key={day} value={day}>
                      {t(`adminDashboard.hostedAgents.routinesTab.weekday.${day}`, {
                        defaultValue: HOSTED_AGENT_WEEKDAY_DEFAULTS[day],
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor={`routine-time-${routine.id}`}>
              {t("adminDashboard.hostedAgents.routinesTab.timeLabel", {
                defaultValue: "Time (UTC)",
              })}
            </Label>
            <Input
              className="w-40"
              id={`routine-time-${routine.id}`}
              type="time"
              value={schedule.time}
              onChange={(event) => update({ time: event.target.value })}
            />
          </div>
        </div>
      ) : null}

      {schedule.frequency === "advanced" ? (
        <div className="space-y-2">
          <Label htmlFor={`routine-cron-${routine.id}`}>
            {t("adminDashboard.hostedAgents.routinesTab.cronLabel", {
              defaultValue: "Cron expression",
            })}
          </Label>
          <Input
            aria-invalid={scheduleInvalid}
            className="font-mono"
            id={`routine-cron-${routine.id}`}
            placeholder="0 8 * * 1-5"
            value={schedule.cron}
            onChange={(event) => update({ cron: event.target.value })}
          />
          <p className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.routinesTab.cronHelp", {
              defaultValue:
                "Standard 5-field crontab in UTC: minute hour day-of-month month day-of-week. Example: 0 8 * * 1-5 = 08:00 on weekdays.",
            })}
          </p>
        </div>
      ) : null}

      {scheduleInvalid ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs">
          {t("adminDashboard.hostedAgents.routinesTab.scheduleInvalid", {
            defaultValue: "Set a valid schedule (an interval of 1+ minutes or a cron expression).",
          })}
        </p>
      ) : null}
    </div>
  )
}
